import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Button, Checkbox, FormControlLabel, Typography } from "@mui/material";
import toast from "react-hot-toast";
import { MainContainer } from "../../components/ContentWrapper";
import { PageTitle } from "../../components/PageTitle";
import axiosInstant from "../../api";
import {
    ALWAYS_ON_FILE_TYPES,
    BRAND_FILE_TYPE_CONFIG,
    DownloadType,
} from "../admin/videos/selectedVideoCardV2/helpers";

export default function BrandFileTypesPage() {
    const { id } = useParams<{ id: string }>();
    const [enabled, setEnabled] = useState<string[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!id) return;
        setLoading(true);
        axiosInstant
            .get(`/brands/${id}`)
            .then((res) => {
                const list = Array.isArray(res.data?.enabled_file_types)
                    ? res.data.enabled_file_types
                    : [];
                setEnabled(list);
            })
            .catch(() => setEnabled([]))
            .finally(() => setLoading(false));
    }, [id]);

    const toggle = (key: string) => {
        setEnabled((prev) =>
            prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
        );
    };

    const handleSave = async () => {
        if (!id) return;
        setSaving(true);
        try {
            const fileTypes = enabled.filter(
                (k) => k !== DownloadType.HIGH_RES,
            );
            // Locked-on types (Watermarked/Thumbnails/Transcripts) are always
            // stored; the API enforces this too. HD Original is always
            // available but is not a storable key.
            for (const locked of ALWAYS_ON_FILE_TYPES) {
                if (
                    locked !== DownloadType.HIGH_RES &&
                    !fileTypes.includes(locked)
                ) {
                    fileTypes.push(locked);
                }
            }
            await axiosInstant.put(`/brands/${id}/file-selections`, {
                file_types: fileTypes,
            });
            toast.success("File type selections saved successfully.");
        } catch (err) {
            console.error("Failed to save file selections:", err);
            toast.error("Failed to save file type selections.");
        } finally {
            setSaving(false);
        }
    };

    if (!id) {
        return (
            <MainContainer>
                <Typography color="error">Brand ID is required.</Typography>
            </MainContainer>
        );
    }

    return (
        <MainContainer>
            <Box className="flex justify-between items-center mb-6">
                <PageTitle title="Brand File Types" />
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={saving || loading}
                >
                    {saving ? "Saving..." : "Save Changes"}
                </Button>
            </Box>

            <Box className="mb-4">
                <Typography variant="body2" color="textSecondary">
                    Select which file types are available to download for this brand.
                    Only HD Web Optimized and HD WebM can be turned off; all
                    other file types are always available.
                </Typography>
            </Box>

            <Box className="flex flex-col gap-2">
                {BRAND_FILE_TYPE_CONFIG.map((ft) => {
                    const checked = ft.always || enabled.includes(ft.key);
                    return (
                        <Box
                            key={ft.key}
                            sx={{
                                border: "1px solid var(--border)",
                                borderRadius: "8px",
                                px: 2,
                                py: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                            }}
                        >
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={checked}
                                        onChange={() =>
                                            !ft.always && toggle(ft.key)
                                        }
                                        disabled={!!ft.always}
                                    />
                                }
                                label=""
                                sx={{ mr: 0 }}
                            />
                            <Box>
                                <Typography fontWeight={600}>
                                    {ft.title}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{ color: "var(--text-2)", fontSize: 12 }}
                                >
                                    {ft.description}
                                </Typography>
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </MainContainer>
    );
}
